import { createContext, useContext, useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import {
  supabase,
  isSupabaseConfigured,
  clearAuthStorage,
} from "../lib/supabase";
import {
  showGlobalLoading,
  hideGlobalLoading,
} from "../components/GlobalLoadingIndicator";
import { clearCurrentWorkerCache } from "../services/current-worker";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{
    error: Error | null;
    user: User | null;
  }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      try {
        setUser(session?.user ?? null);
      } catch (error) {
        console.error("Error setting user session:", error);
      } finally {
        setLoading(false);
        hideGlobalLoading();
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state change:", event, session?.user.id);

      // Handle different auth events
      if (event === "SIGNED_OUT") {
        setUser(null);
        clearCurrentWorkerCache();
        // Ensure clean logout by clearing any remaining storage
        try {
          Object.keys(localStorage).forEach((key) => {
            if (
              key.includes("supabase") ||
              key.includes("auth") ||
              key.startsWith("sb-")
            ) {
              localStorage.removeItem(key);
            }
          });
        } catch (error) {
          console.warn("Error clearing storage on sign out:", error);
        }
      } else {
        setUser(session?.user ?? null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    showGlobalLoading();
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Sign in error:", error);
        return { error, user: null };
      }

      if (!data.user) {
        console.error("No user data returned");
        return {
          error: new Error("Invalid response from authentication service"),
          user: null,
        };
      }

      return { error: null, user: data.user };
    } catch (error) {
      console.error("Unexpected error during sign in:", error);
      return {
        error: new Error("Authentication failed - please try again"),
        user: null,
      };
    } finally {
      hideGlobalLoading();
    }
  };

  const signOut = async () => {
    console.log("Starting sign out process...");
    showGlobalLoading();

    try {
      // First, immediately clear the user state to prevent UI inconsistencies
      setUser(null);
      clearCurrentWorkerCache();

      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error("Supabase sign out error:", error);
        // Don't throw here - we still want to clean up local state
      }

      // Force clear all auth-related storage
      clearAuthStorage();

      console.log("Sign out completed successfully");
    } catch (error) {
      console.error("Unexpected error during sign out:", error);
      // Even if there's an error, we still want to clear local state
      setUser(null);
    } finally {
      hideGlobalLoading();
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
